import i18next from 'i18next';

import type {
    MergedPlanValidationError,
    MergedPlanValidationResult,
    PlanValidationError,
    PlanValidationResult,
} from '../types';

export const transformPlanValidationResultsToMergedPlanValidationResults = (
    planValidationResults: PlanValidationResult[],
): MergedPlanValidationResult[] => {
    if (planValidationResults.every(result => result.type === 'success')) {
        return planValidationResults;
    }

    const mergedPlanValidationErrors = planValidationResults.reduce<
        Record<PlanValidationError['error'] | 'server-error', MergedPlanValidationError>
    >((acc, validationResult) => {
        if (validationResult.type === 'error') {
            validationResult.errors.map(error => {
                const errorRecord = acc[error.error];
                if (errorRecord && error.metadata && errorRecord.type === 'error') {
                    errorRecord.childIds = [...errorRecord.childIds, error.metadata.childId];
                    errorRecord.message = errorMessage(error, true) ?? errorRecord.message;
                } else {
                    acc[error.error] = {
                        type: 'error',
                        message: errorMessage(error, false) ?? error.message,
                        childIds: error.metadata ? [error.metadata.childId] : [],
                        error: error.error,
                    };
                }
            });
        } else if (validationResult.type === 'server-error') {
            if (!acc['server-error']) {
                acc['server-error'] = {
                    type: 'server-error',
                    message: validationResult.message,
                    error: validationResult.error,
                };
            }
        }
        // in case of errors, we don't care about returning the success results
        return acc;
    }, {} as Record<PlanValidationError['error'] | 'server-error', MergedPlanValidationError>);

    return Object.values(mergedPlanValidationErrors);
};

const errorMessage = (error: PlanValidationError, plural: boolean): string | null => {
    switch (error.error) {
        case 1614173107: {
            if (error.metadata?.endDate && !plural) {
                return i18next.t('react.simplifiedUsBillingPlan.translations.json.childrenErrors.lastDayConflict', {
                    count: 1,
                    childLastDay: error.metadata.endDate,
                });
            }
            return i18next.t('react.simplifiedUsBillingPlan.translations.json.childrenErrors.lastDayConflict', {
                count: 2,
            });
        }
        case 1546519543: {
            if (error.metadata?.startDate && !plural) {
                return i18next.t('react.simplifiedUsBillingPlan.translations.json.childrenErrors.startDateConflict', {
                    count: 1,
                    childFirstDay: error.metadata.startDate,
                });
            }
            return i18next.t('react.simplifiedUsBillingPlan.translations.json.childrenErrors.startDateConflict', {
                count: 2,
            });
        }
        case 1494934257: {
            if (error.metadata?.endDate && !plural) {
                return i18next.t(
                    'react.simplifiedUsBillingPlan.translations.json.childrenErrors.planStartsAfterChildLastDay',
                    {
                        count: 1,
                        childLastDay: error.metadata.endDate,
                    },
                );
            }
            return i18next.t(
                'react.simplifiedUsBillingPlan.translations.json.childrenErrors.planStartsAfterChildLastDay',
                {
                    count: 2,
                },
            );
        }
        default:
            return null;
    }
};
